import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_ORG_CALLS } from '../../../lib/graphql/queries/organizations';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

const PAGE_SIZE = 25;

export const useOrgCallsGQL = ({
  dateRange,
  enabled = true,
  pageSize = PAGE_SIZE,
  hasTransfer,
  answeredBy,
  outcomes,
  sortBy = 'durationSeconds',
  sortOrder = 'DESC',
}: {
  dateRange: DateRange;
  enabled?: boolean;
  pageSize?: number;
  hasTransfer?: boolean;
  answeredBy?: string;
  outcomes?: string[];
  sortBy?: string | undefined;
  sortOrder?: string | undefined;
}) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';

  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [currentSortBy, setCurrentSortBy] = useState(sortBy);
  const [currentSortOrder, setCurrentSortOrder] = useState(sortOrder);
  const [currentSearch, setCurrentSearch] = useState<string | undefined>(undefined);
  const [currentAnsweredBy, setCurrentAnsweredBy] = useState<string | undefined>(
    answeredBy
  );
  // Modal-level overrides — when set, replace the inherited dateRange / add a
  // flowName filter. Date range falls back to the prop-passed dateRange so the
  // dashboard's selection is the default.
  const [currentDateFrom, setCurrentDateFrom] = useState<string | undefined>(undefined);
  const [currentDateTo, setCurrentDateTo] = useState<string | undefined>(undefined);
  const [currentFlowName, setCurrentFlowName] = useState<string | undefined>(undefined);
  const [currentFlowIds, setCurrentFlowIds] = useState<string[] | undefined>(undefined);
  const [currentFlowTypes, setCurrentFlowTypes] = useState<string[] | undefined>(undefined);
  const [currentIncludeInbound, setCurrentIncludeInbound] = useState<boolean | undefined>(
    undefined
  );

  const dateFrom = currentDateFrom ?? dateRange.from?.toISOString() ?? undefined;
  const dateTo = currentDateTo ?? dateRange.to?.toISOString() ?? undefined;

  const { data, loading } = useQuery(GET_ORG_CALLS, {
    variables: {
      organizationId: orgId,
      limit: currentPageSize,
      offset: currentPage * currentPageSize,
      dateFrom,
      dateTo,
      sortBy: currentSortBy,
      sortOrder: currentSortOrder,
      // includeTest intentionally omitted — show all calls (test + real).
      // Test calls are surfaced via a badge in the row, not filtered out.
      ...(hasTransfer !== undefined && { hasTransfer }),
      ...(currentSearch ? { search: currentSearch } : {}),
      ...(currentAnsweredBy ? { answeredBy: currentAnsweredBy } : {}),
      ...(currentFlowName ? { flowName: currentFlowName } : {}),
      ...(currentFlowIds && currentFlowIds.length ? { flowIds: currentFlowIds } : {}),
      ...(currentFlowTypes && currentFlowTypes.length ? { flowTypes: currentFlowTypes } : {}),
      ...(currentIncludeInbound ? { includeInbound: true } : {}),
      ...(outcomes ? { outcomes } : {}),
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  type OrgCallsData = { organization?: { calls?: { items?: unknown[]; total?: number; hasMore?: boolean } } };
  const d = data as OrgCallsData;
  const calls = d?.organization?.calls?.items ?? [];
  const total = d?.organization?.calls?.total ?? 0;
  const hasMore = d?.organization?.calls?.hasMore ?? false;

  const loadMore = useCallback(() => {
    setCurrentPage((p) => p + 1);
  }, []);

  const refresh = useCallback(() => {
    setCurrentPage(0);
  }, []);

  const loadPage = useCallback((newOffset: number, newLimit: number) => {
    if (newLimit !== currentPageSize) {
      setCurrentPageSize(newLimit);
      setCurrentPage(0);
    } else {
      setCurrentPage(Math.floor(newOffset / newLimit));
    }
  }, [currentPageSize]);

  const sort = useCallback((newSortBy: string, newSortOrder: string) => {
    setCurrentSortBy(newSortBy);
    setCurrentSortOrder(newSortOrder);
    setCurrentPage(0);
  }, []);

  const filter = useCallback((params: {
    search?: string;
    answeredBy?: string;
    flowName?: string;
    flowIds?: string[];
    flowTypes?: string[];
    includeInbound?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    setCurrentSearch(params.search);
    setCurrentAnsweredBy(params.answeredBy);
    setCurrentFlowName(params.flowName);
    setCurrentFlowIds(params.flowIds);
    setCurrentFlowTypes(params.flowTypes);
    setCurrentIncludeInbound(params.includeInbound);
    setCurrentDateFrom(params.dateFrom);
    setCurrentDateTo(params.dateTo);
    setCurrentPage(0);
  }, []);

  return {
    calls,
    total,
    hasMore,
    conversationsTotal: 0,
    isLoading: loading,
    loadMore,
    loadPage,
    refresh,
    sort,
    filter,
  };
};
